import { Box, IconButton, Button, Tooltip } from '@mui/material';
import { Restore } from '@mui/icons-material';
import { SoftDeleteType } from '../../models/soft-deletes';

// Util to truncate long text and on hoover show full text on tooltip
const truncateTxtWithTooltip = (
  text: string | number | null | undefined, 
  maxWidth: number = 200
) => (
  <Tooltip title={text ?? ''} arrow>
    <Box 
      component="span" 
      sx={{ 
        display: 'inline-block', 
        maxWidth, 
        whiteSpace: 'nowrap', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis' 
      }}
    >
      {text ?? ''}
    </Box>
  </Tooltip>
);

export const createPersonColumns = (
  handleRestore: (id: number, type: string) => void,
  formatDate: (dateValue: string | null) => string
) => [
  {
    accessorKey: 'personId',
    header: 'ID',
    enableSorting: false,
    enableHiding: false, // Always visible
    enableColumnFilter: false,
    Cell: ({ row }: any) => {
      const original = row.original;
      return original.legacyUri ? (
        <Tooltip title={`Open Person ${original.personId} in legacy system`} arrow>
          <Button
            variant="text"
            onClick={() => window.open(original.legacyUri!, '_blank', 'noopener,noreferrer')}
            sx={{ 
              p: 0, 
              textTransform: 'none', 
              textDecoration: 'underline', 
              minWidth: 0, 
              fontWeight: 500, 
              color: '#1976d2', 
              cursor: 'pointer', 
              fontSize: 'inherit',
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                textDecoration: 'underline',
              },
            }}
          >
            {original.personId}
          </Button>
        </Tooltip>
      ) : (
        <Box component="span">{original.personId}</Box>
      );
    },
  },
  {
    accessorKey: 'firstName',
    header: 'First Name',
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'lastName',
    header: 'Last Name',
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'company',
    header: 'Company',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'email',
    header: 'Email',
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'lastTouchedBy',
    header: 'Last Touched By',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'activities',
    header: 'Activities',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'isVip',
    header: 'VIPs',
    enableSorting: false,
    enableHiding: true,
    Cell: ({ cell }: any) => (cell.getValue() as boolean ? 'Yes' : 'No'),
  },
  {
    accessorKey: 'deletedAt',
    header: 'Date of Deletion',
    enableSorting: true,
    enableHiding: true,
    Cell: ({ cell }: any) => formatDate(cell.getValue() as string),
  },
  {
    id: 'actions',
    header: 'Restore',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false, // Always visible
    Cell: ({ row }: any) => (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Restore this person" arrow>
          <IconButton
            type="button"
            onClick={() => handleRestore(row.original.personId, SoftDeleteType.Person)}
            color="primary"
            size="small"
            disabled={false}
          >
            <Restore fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];

export const createPropertyColumns = (
  handleRestore: (id: number, type: string) => void,
  formatDate: (dateValue: string | null) => string
) => [
  {
    accessorKey: 'propertyId',
    header: 'ID',
    enableSorting: false,
    enableHiding: false, // Always visible
    enableColumnFilter: false,
    Cell: ({ row }: any) => {
      const original = row.original;
      return original.legacyUri ? (
        <Tooltip title={`Open Property ${original.propertyId} in legacy system`} arrow>
          <Button
            variant="text"
            onClick={() => window.open(original.legacyUri!, '_blank', 'noopener,noreferrer')}
            sx={{ 
              p: 0, 
              textTransform: 'none', 
              textDecoration: 'underline', 
              minWidth: 0, 
              fontWeight: 500, 
              color: '#1976d2', 
              cursor: 'pointer', 
              fontSize: 'inherit',
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                textDecoration: 'underline',
              },
            }}
          >
            {original.propertyId}
          </Button>
        </Tooltip>
      ) : (
        <Box component="span">{original.propertyId}</Box>
      );
    },
  },
  {
    accessorKey: 'name',
    header: 'Property Name',
    enableSorting: true,
    enableHiding: true,
  },
  {
    accessorKey: 'address',
    header: 'Address',
    enableSorting: false,
    enableHiding: true,
    Cell: ({ cell }: any) => truncateTxtWithTooltip(cell.getValue() as string | null, 200),
  },
  {
    accessorKey: 'city',
    header: 'City',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'state',
    header: 'State',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'zip',
    header: 'Zip',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'propertyType',
    header: 'Property Type',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'units',
    header: 'Units',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'beds',
    header: 'Beds',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'buildableArea',
    header: 'Building Size',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'lotAcres',
    header: 'Lot Size',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'yearBuilt',
    header: 'Year Built',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'lastSaleDate',
    header: 'Last Sale Date',
    enableSorting: false,
    enableHiding: true,
    Cell: ({ cell }: any) => formatDate(cell.getValue() as string | null),
  },
  {
    accessorKey: 'deletedAt',
    header: 'Date of Deletion',
    enableSorting: true,
    enableHiding: true,
    Cell: ({ cell }: any) => formatDate(cell.getValue() as string),
  },
  {
    id: 'actions',
    header: 'Restore',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false, // Always visible
    Cell: ({ row }: any) => (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Restore this property" arrow>
          <IconButton
            type="button"
            onClick={() => handleRestore(row.original.propertyId, SoftDeleteType.Property)}
            color="primary"
            size="small"
            disabled={false}
          >
            <Restore fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];

export const createCompanyColumns = (
  handleRestore: (id: number, type: string) => void,
  formatDate: (dateValue: string | null) => string
) => [
  {
    accessorKey: 'organizationId',
    header: 'ID',
    enableSorting: false,
    enableHiding: false, // Always visible
    enableColumnFilter: false,
    Cell: ({ row }: any) => {
      const original = row.original;
      return original.legacyUri ? (
        <Tooltip title={`Open Company ${original.organizationId} in legacy system`} arrow>
          <Button
            variant="text"
            onClick={() => window.open(original.legacyUri!, '_blank', 'noopener,noreferrer')}
            sx={{ 
              p: 0, 
              textTransform: 'none', 
              textDecoration: 'underline', 
              minWidth: 0, 
              fontWeight: 500, 
              color: '#1976d2', 
              cursor: 'pointer', 
              fontSize: 'inherit',
              textAlign: 'left',
              justifyContent: 'flex-start',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                textDecoration: 'underline',
              },
            }}
          >
            {original.organizationId}
          </Button>
        </Tooltip>
      ) : (
        <Box component="span">{original.organizationId}</Box>
      );
    },
  },
  {
    accessorKey: 'name',
    header: 'Company Name',
    enableSorting: true,
    enableHiding: true,
    Cell: ({ cell }: any) => truncateTxtWithTooltip(cell.getValue() as string, 250),
  },
  {
    accessorKey: 'city',
    header: 'City',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'state',
    header: 'State',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'companyType',
    header: 'Company Type',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'assetClassIds',
    header: 'Asset Class',
    enableSorting: false,
    enableHiding: true,
    Cell: ({ cell }: any) => {
      const value = cell.getValue() as number[];
      if (!value || value.length === 0) return '';
      return value.join(', ');
    },
  },
  {
    accessorKey: 'unitCount',
    header: 'Unit Count',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'transactionVolume',
    header: 'Transaction Volume',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'retailSquareFootage',
    header: 'Retail Sq Ft',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'numberOfAssets',
    header: '# of Assets',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'numberOfPeople',
    header: '# of People',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'lastTouchedAt',
    header: 'Last Touched Date',
    enableSorting: false,
    enableHiding: true,
    Cell: ({ cell }: any) => formatDate(cell.getValue() as string | null),
  },
  {
    accessorKey: 'lastTouchedBy',
    header: 'Last Touched By',
    enableSorting: false,
    enableHiding: true,
  },
  {
    accessorKey: 'deletedAt',
    header: 'Date of Deletion',
    enableSorting: true,
    enableHiding: true,
    Cell: ({ cell }: any) => formatDate(cell.getValue() as string),
  },
  {
    id: 'actions',
    header: 'Restore',
    enableSorting: false,
    enableColumnFilter: false,
    enableHiding: false, // Always visible
    Cell: ({ row }: any) => (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tooltip title="Restore this company" arrow>
          <IconButton
            type="button"
            onClick={() => handleRestore(row.original.organizationId, SoftDeleteType.Company)}
            color="primary"
            size="small"
            disabled={false}
          >
            <Restore fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    ),
  },
];
